/**
 * PayU redirect flow: the browser must POST the merchant `key` to PayU.
 * CRA inlines REACT_APP_* at compile time - restart `npm start` after editing `.env*`.
 */

const ENV_KEYS = [
  'REACT_APP_PAYU_MERCHANT_KEY',
  'REACT_APP_PAYU_KEY',
  'REACT_APP_PAYU_MKEY',
] as const;

/**
 * PayU sandbox merchant key for this project (test.payu.in).
 * Used only when NODE_ENV === 'development' and no REACT_APP_* key was inlined.
 */
const PAYU_DEV_MERCHANT_KEY_FALLBACK = 'ZfPHCx';

function readPayUMerchantKeyFromProcessEnv(): string | undefined {
  const env = process.env as Record<string, string | undefined>;
  for (const k of ENV_KEYS) {
    const t = env[k]?.trim();
    if (t) return t;
  }
  return undefined;
}

export function getPayUMerchantKeyFromEnv(): string | undefined {
  const fromEnv = readPayUMerchantKeyFromProcessEnv();
  if (fromEnv) return fromEnv;
  if (
    process.env.NODE_ENV === 'development' &&
    PAYU_DEV_MERCHANT_KEY_FALLBACK.trim().length > 0
  ) {
    return PAYU_DEV_MERCHANT_KEY_FALLBACK.trim();
  }
  return undefined;
}

function tryParseObjectRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  if (typeof v === 'string') {
    try {
      const p = JSON.parse(v) as unknown;
      if (p && typeof p === 'object' && !Array.isArray(p)) {
        return p as Record<string, unknown>;
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

/**
 * Flattens common create-order response shapes so Checkout can read `key`, `hash`, etc.
 */
export function normalizePaymentCreateOrderPayload(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  let base: Record<string, unknown> = { ...(raw as Record<string, unknown>) };

  const unwrapKeys = ['data', 'result', 'payload', 'body', 'payment', 'order'] as const;
  for (const uk of unwrapKeys) {
    const inner = tryParseObjectRecord(base[uk]);
    if (inner) {
      base = { ...base, ...inner };
    }
  }

  const stringBlobKeys = [
    'payu_form',
    'payuForm',
    'form_fields',
    'formFields',
    'payu_params',
    'payuParams',
    'payment_params',
    'paymentParams',
  ] as const;
  for (const sk of stringBlobKeys) {
    const inner = tryParseObjectRecord(base[sk]);
    if (inner) {
      base = { ...base, ...inner };
    }
  }

  // Drop wrapper objects so UI code never reads duplicate keys from nested trees (hash mismatch).
  for (const k of [...unwrapKeys, ...stringBlobKeys]) {
    delete base[k];
  }

  // Preserve server-signed POST map (never strip - used as sole source for PayU form).
  // (If it lived only under `data`, merge above already lifted it to root.)

  return base;
}

/**
 * PayU may return hash as a hex string or as `{ v1, v2 }` (newer dashboards).
 */
export function extractPayURequestHash(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return undefined;
    if (t.startsWith('{') && t.includes('"v1"')) {
      try {
        const p = JSON.parse(t) as { v1?: string };
        if (p.v1?.trim()) return p.v1.trim().toLowerCase();
      } catch {
        /* ignore */
      }
    }
    return t.toLowerCase();
  }
  if (typeof value === 'object' && value !== null && 'v1' in value) {
    const v1 = (value as { v1?: unknown }).v1;
    if (typeof v1 === 'string' && v1.trim()) return v1.trim().toLowerCase();
  }
  return undefined;
}
